import { useEffect } from 'react';
import { TimelineState } from '../types/timeline';
import { VideoFile, ImageFile } from '../types';

interface UseStudioHotkeysProps {
    activeTab: string;
    sourceMedia: any; // Relaxed type to avoid RushMedia conflicts during refactor
    timelineState: TimelineState;
    setTimelineState: React.Dispatch<React.SetStateAction<TimelineState>>;
    handleUndo: () => void;
    handleRedo: () => void;
    togglePlayPause: () => void;
    handleSplitSegment: (id: string, time: number) => void;
    handleDeleteGap: (time: number, trackId: string) => void;
    handleSegmentDelete: (id: string, ripple: boolean) => void;
    selectedGap: { inSec: number; trackId: string } | null;
    FPS?: number;
    currentTime: number;
    seek: (time: number) => void;
    handleSeekForward?: () => void;
    handleSeekBackward?: () => void;
}

export const useStudioHotkeys = ({
    activeTab,
    sourceMedia,
    timelineState,
    setTimelineState,
    handleUndo,
    handleRedo,
    togglePlayPause,
    handleSplitSegment,
    handleDeleteGap,
    handleSegmentDelete,
    selectedGap,
    FPS = 30,
    currentTime,
    seek,
    handleSeekForward,
    handleSeekBackward
}: UseStudioHotkeysProps) => {
    const frameStep = 1 / FPS;

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore if typing in an input
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if ((e.target as HTMLElement).isContentEditable) return;

            // === GLOBAL SHORTCUTS (UNDO/REDO) ===
            const isUndo = (e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey) && !e.shiftKey;
            const isRedo = ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey) && e.shiftKey) ||
                ((e.key === 'y' || e.key === 'Y') && (e.ctrlKey || e.metaKey));

            if (isUndo) {
                e.preventDefault();
                handleUndo();
                return;
            }
            if (isRedo) {
                e.preventDefault();
                handleRedo();
                return;
            }

            // === EDITING TAB ONLY SHORTCUTS ===
            if (activeTab !== 'editing') return;

            // NLE: When SourceViewer is open, allow Delete/Backspace for timeline but block other keys
            // (SourceViewer handles V, B, I, O, Space, arrows)
            if (sourceMedia) {
                const deleteKeys = ['Delete', 'Backspace'];
                if (!deleteKeys.includes(e.key)) return;
            }

            const { selectedSegmentIds, segments, playheadSec } = timelineState;
            const selectedId = selectedSegmentIds.length > 0 ? selectedSegmentIds[0] : null;

            // Calculate total duration
            const totalDuration = segments.length > 0
                ? Math.max(...segments.map(s => s.outSec))
                : 30;

            switch (e.key) {
                // === FRAME NAVIGATION ===
                case 'ArrowLeft':
                    e.preventDefault();
                    if (e.ctrlKey || e.metaKey) {
                        if (handleSeekBackward) handleSeekBackward();
                        else seek(currentTime - (10 / FPS));
                    } else {
                        const stepBack = e.shiftKey ? 1 : frameStep;
                        seek(Math.max(0, currentTime - stepBack));
                    }
                    break;

                case 'ArrowRight':
                    e.preventDefault();
                    if (e.ctrlKey || e.metaKey) {
                        if (handleSeekForward) handleSeekForward();
                        else seek(currentTime + (10 / FPS));
                    } else {
                        const stepForward = e.shiftKey ? 1 : frameStep;
                        seek(Math.min(totalDuration, currentTime + stepForward));
                    }
                    break;

                case 'Home':
                    e.preventDefault();
                    seek(0);
                    break;

                case 'End':
                    e.preventDefault();
                    seek(totalDuration);
                    break;

                // === SPLIT/RAZOR (X) - Single segment or Shift+X for all tracks ===
                case 'x':
                case 'X':
                    e.preventDefault();
                    if (e.shiftKey) {
                        // SHIFT+X: Cut ALL segments across ALL tracks at playhead
                        const segmentsAtPlayhead = segments.filter(s =>
                            playheadSec > s.inSec && playheadSec < s.outSec
                        );
                        console.log(`[Razor] Shift+X: Cutting ${segmentsAtPlayhead.length} segments at ${playheadSec.toFixed(2)}s`);
                        for (const seg of segmentsAtPlayhead) {
                            const segTrack = timelineState.tracks.find(t => t.id === seg.trackId);
                            if (!seg.locked && !segTrack?.locked) {
                                handleSplitSegment(seg.id, playheadSec);
                            }
                        }
                    } else {
                        // X: Cut single segment at playhead
                        const segmentAtPlayhead = segments.find(s =>
                            playheadSec > s.inSec && playheadSec < s.outSec
                        );
                        if (segmentAtPlayhead) {
                            const segTrack = timelineState.tracks.find(t => t.id === segmentAtPlayhead.trackId);
                            if (!segmentAtPlayhead.locked && !segTrack?.locked) {
                                handleSplitSegment(segmentAtPlayhead.id, playheadSec);
                            }
                        }
                    }
                    break;

                // === DELETE (Delete/Backspace) - Shift for ripple delete ===
                case 'Delete':
                case 'Backspace':
                    e.preventDefault();
                    // GAP DELETE
                    if (selectedGap) {
                        handleDeleteGap(selectedGap.inSec + 0.01, selectedGap.trackId);
                        return;
                    }
                    // NLE: Delete ALL selected segments (multi-select support)
                    if (timelineState.selectedSegmentIds.length > 0) {
                        // Collect all segments to delete (including linked clips)
                        const idsToDelete = new Set<string>();
                        for (const id of timelineState.selectedSegmentIds) {
                            const segment = segments.find(s => s.id === id);
                            if (!segment) continue;

                            const segTrack = timelineState.tracks.find(t => t.id === segment.trackId);
                            if (segment.locked || segTrack?.locked) continue;

                            // Add segment and its linked clips
                            if (segment.linkGroupId) {
                                segments.filter(s => s.linkGroupId === segment.linkGroupId)
                                    .forEach(s => idsToDelete.add(s.id));
                            } else {
                                idsToDelete.add(id);
                            }
                        }

                        // Delete each unique segment (linked clips handled by handleSegmentDelete)
                        const uniqueIds = Array.from(idsToDelete);
                        if (uniqueIds.length > 0) {
                            // Use first segment's ID - handleSegmentDelete will collect linked clips
                            handleSegmentDelete(uniqueIds[0], e.shiftKey);

                            // For multi-select without linkGroup, delete remaining individually
                            if (uniqueIds.length > 1) {
                                for (let i = 1; i < uniqueIds.length; i++) {
                                    handleSegmentDelete(uniqueIds[i], e.shiftKey);
                                }
                            }
                            console.log(`[NLE Delete] Deleted ${uniqueIds.length} segment(s)`);
                        }
                    }
                    break;

                // === PLAY/PAUSE (Space) ===
                case ' ':
                    e.preventDefault();
                    togglePlayPause();
                    break;

                // === MARK IN/OUT (I/O) - for selected segment ===
                case 'i':
                case 'I':
                    if (selectedId) {
                        setTimelineState(prev => ({
                            ...prev,
                            segments: prev.segments.map(s => s.id === selectedId
                                ? { ...s, inSec: playheadSec, durationSec: s.outSec - playheadSec }
                                : s)
                        }));
                        e.preventDefault();
                    }
                    break;

                case 'o':
                case 'O':
                    if (selectedId) {
                        setTimelineState(prev => ({
                            ...prev,
                            segments: prev.segments.map(s => s.id === selectedId
                                ? { ...s, outSec: playheadSec, durationSec: playheadSec - s.inSec }
                                : s)
                        }));
                        e.preventDefault();
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [timelineState, activeTab, sourceMedia, handleSegmentDelete, handleSplitSegment, frameStep, handleUndo, handleRedo, selectedGap, handleDeleteGap, togglePlayPause, setTimelineState, currentTime, seek, handleSeekForward, handleSeekBackward, FPS]);
};
